import { useCallback } from "react"
import {
  GetDashboardDocument,
  UpdateAlertDocument,
  UpdateAlertMutation,
  UpdateAlertMutationVariables,
  AlertSeverityEnum,
  AlertStatusEnum,
  AlertTypeEnum,
  AlertSourceEnum,
  GetSystemAlertsDocument,
} from "@hydrologiq/gql-experience/graphql"
import { useMutationWithEndpoint } from "@hydrologiq/shared/hooks/useGraphQLClient"
import { toaster } from "@hydrologiq/shared/chakra/Toaster"

export interface UpdateAlertInput {
  name?: string
  severity?: "critical" | "warning" | "info"
  status?: "new" | "acknowledged" | "attended" | "on_hold" | "completed"
  note?: string
  /** Set to `null` to clear assignee; omit to leave unchanged. */
  assignedToUserId?: string | null
  /** Admin-only: update alert metadata */
  metadata?: Record<string, unknown> | null
}

export function useUpdateAlert(dashboardId?: string, systemId?: string) {
  const refetchQueries = []
  if (dashboardId) refetchQueries.push({ query: GetDashboardDocument, variables: { dashboardId } })
  if (systemId) refetchQueries.push({ query: GetSystemAlertsDocument, variables: { systemId } })
  const [updateAlert, { loading }] = useMutationWithEndpoint<UpdateAlertMutation, UpdateAlertMutationVariables>(
    UpdateAlertDocument,
    {
      refetchQueries,
      awaitRefetchQueries: true,
      optimisticResponse: (variables) => ({
        updateAlert: {
          __typename: "AlertFull" as const,
          id: variables.input.id,
          name: variables.input.name || "",
          severity: variables.input.severity || AlertSeverityEnum.Info,
          type: AlertTypeEnum.Status,
          status: "new" as any,
          source: AlertSourceEnum.Internal,
          installation: null,
          installation_name: null,
          installation_group_id: null,
          installation_group_name: null,
          message: "",
          metadata: variables.input.metadata !== undefined ? variables.input.metadata : null,
          fired_at: "",
          resolved_at: null,
          created_at: "",
          assets: [],
          asset_count: 0,
          events: [],
          severity_rank: null,
          status_rank: null,
          hours_since_fired: null,
          resolution_time_hours: null,
          customer_site_context: null,
          assignee: null,
          system_count: 0,
          systems: [],
        },
      }),
      update: (cache, { data }) => {
        if (data?.updateAlert) {
          cache.modify({
            fields: {
              getAlerts(existingAlerts = {}) {
                return existingAlerts
              },
            },
          })
        }
      },
    },
    "primary",
  )

  const update = useCallback(
    async (alertId: string, input: UpdateAlertInput) => {
      try {
        const gqlInput: UpdateAlertMutationVariables["input"] = { id: alertId }
        if (input.name !== undefined) gqlInput.name = input.name
        if (input.severity !== undefined) {
          gqlInput.severity = input.severity as AlertSeverityEnum
        }
        if (input.status !== undefined) {
          gqlInput.status = input.status as AlertStatusEnum
        }
        if (input.note !== undefined) gqlInput.note = input.note
        if (input.assignedToUserId !== undefined) {
          gqlInput.assignedToUserId = input.assignedToUserId
        }
        if (input.metadata !== undefined) {
          gqlInput.metadata = input.metadata
        }
        await updateAlert({
          variables: { input: gqlInput },
        })
        toaster.create({
          title: "Alert updated successfully",
          type: "success",
          duration: 5000,
        })
      } catch (error) {
        toaster.create({
          title: "Failed to update alert",
          description: error instanceof Error ? error.message : "An unexpected error occurred",
          type: "error",
          duration: 5000,
        })
        throw error
      }
    },
    [updateAlert],
  )

  return { update, loading }
}
